import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { LocationSelector } from '@/components/location/LocationSelector';
import { useTrainingModeStore } from '@/store/trainingMode';
import { toast } from 'sonner';
import type { LsNavGroup } from './navConfig';

interface SubNavProps {
  group: LsNavGroup;
}

/**
 * Contextual sub-navigation column for the active rail group. Header shows the
 * section name and a location switcher (mirroring the store/register block in
 * Lightspeed Retail), followed by the section's pages.
 */
export function SubNav({ group }: SubNavProps) {
  const { isTrainingMode } = useTrainingModeStore();

  return (
    <div className="flex h-full w-52 shrink-0 flex-col border-r border-border bg-[hsl(var(--ls-subnav))]">
      <div className="border-b border-border px-3 py-3">
        <p className="px-1 pb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {group.label}
        </p>
        <LocationSelector variant="header" />
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto p-2" aria-label={group.label}>
        {group.items.map((item) => {
          const Icon = item.icon;
          const blocked = isTrainingMode && !item.to.startsWith('/sell');

          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={(e) => {
                if (blocked) {
                  e.preventDefault();
                  toast.error('Navigation Locked', {
                    description: 'Exit Training Mode to navigate to other pages.',
                  });
                }
              }}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  blocked && 'opacity-40 cursor-not-allowed'
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
